import { useMemo } from 'react';

import { GoogleDriveAppSelectorIcon } from '../../../../../common/constants/3p-product-configs/google/qf-icon';
import { SlackAppSelectorIcon } from '../../../../../common/constants/3p-product-configs/slack/qf-icon';
import { RovoLogoFromSVG } from '../../../../../common/constants/icons';
import {
	is3pProductKey,
	type PrimaryProductKey,
	PrimaryProductKeys,
	ProductKeys,
	useProductConfigs,
} from '../../../../../common/constants/products';
import { SEARCH_DIALOG_WIDTH_OBSERVER_ID } from '../../../../../common/constants/quick-find';
import { useElementWidth } from '../../../../../common/ui/width-observer';
import {
	useAvailableProducts,
	usePrimaryProduct,
} from '../../../../../controllers/store/bootstrap';
import { useSelectedProduct } from '../../../../../controllers/store/search';

// TODO: Render dynamic 1P products, hardcoded for now for Team 25
const supported1PApps = (primaryProduct?: PrimaryProductKey) => {
	if (!primaryProduct) {
		return [];
	}

	switch (primaryProduct) {
		case PrimaryProductKeys.Confluence:
			return [{ productKey: ProductKeys.Confluence, redirectToFPS: false }];
		default:
			return [
				{ productKey: ProductKeys.Confluence, redirectToFPS: true },
				{ productKey: ProductKeys.Jira, redirectToFPS: true },
			];
	}
};

// TODO: Render dynamic 1P products, hardcoded for now for Team 25
const supported3PApps = [ProductKeys.GoogleDrive, ProductKeys.Slack];

// TODO: Render the icons based on configuration, hardcoded for now for Team 25
const appSelectorIcons = {
	[ProductKeys.GoogleDrive]: RovoLogoFromSVG(GoogleDriveAppSelectorIcon),
	[ProductKeys.Slack]: RovoLogoFromSVG(SlackAppSelectorIcon),
};

export const useInlineAppSelector: () => {
	apps1PToDisplay: (
		| {
				productConfig: import('../../../../../common/constants/products').ProductConfig;
				productKey: 'confluence';
				redirectToFPS: boolean;
		  }
		| {
				productConfig: import('../../../../../common/constants/products').ProductConfig;
				productKey: 'jira';
				redirectToFPS: boolean;
		  }
	)[];
	apps3PToDisplay: {
		productKey: 'drive' | 'slack';
		productConfig: {
			Icon: import('../../../../../common/constants/icons').RovoLogo;
			displayName: import('react').ReactNode;
			IconBorderless?: import('../../../../../common/constants/icons').RovoLogo;
			is3P?: boolean;
			SearchResultIcon?: import('../../../../../common/constants/icons').RovoLogo;
		};
	}[];
	isDialogAbove400px: boolean;
	isDialogAbove450px: boolean;
	primaryProduct: PrimaryProductKey | undefined;
	selectedProduct: string;
} = () => {
	const [primaryProduct] = usePrimaryProduct();
	const availableProducts = useAvailableProducts();
	const [selectedProduct] = useSelectedProduct();
	const productConfigs = useProductConfigs();

	const dialogWidth = useElementWidth(SEARCH_DIALOG_WIDTH_OBSERVER_ID);
	const isDialogAbove400px = dialogWidth > 400;
	const isDialogAbove450px = dialogWidth > 450;

	const apps3PToDisplay = useMemo(
		() =>
			supported3PApps
				.filter((supported3PApp) => availableProducts.includes(supported3PApp))
				.filter((_) => !selectedProduct)
				.map((productKey) => ({
					productKey,
					productConfig: productConfigs[productKey],
				}))
				// TODO: Remove the following Icon override of the icons when we render the icons based on a configuration
				.map(({ productKey, productConfig }) => ({
					productKey,
					productConfig: {
						...productConfig,
						...(appSelectorIcons[productKey] ? { Icon: appSelectorIcons[productKey] } : {}),
					},
				})),
		[availableProducts, productConfigs, selectedProduct],
	);

	const apps1PToDisplay = useMemo(
		() =>
			supported1PApps(primaryProduct)
				.filter((supported1PApp) => availableProducts.includes(supported1PApp.productKey))
				.filter(
					(supported1PApp) => !selectedProduct || supported1PApp.productKey === selectedProduct,
				)
				.map((supportedApp) => ({
					...supportedApp,
					productConfig: productConfigs[supportedApp.productKey],
				})),
		[availableProducts, primaryProduct, productConfigs, selectedProduct],
	);

	return {
		apps1PToDisplay,
		apps3PToDisplay,
		isDialogAbove400px,
		isDialogAbove450px,
		primaryProduct,
		selectedProduct,
	};
};

const prioritised3PApps: ProductKeys[] = [ProductKeys.GoogleDrive, ProductKeys.Slack];

export const useInlineAppSelectorWithFilterDiscoverability: () => {
	apps3PToDisplay: {
		productKey: import('../../../../../common/constants/products').ProductKeys3P;
		productConfig: import('../../../../../common/constants/products').ProductConfig;
	}[];
	primaryProduct: PrimaryProductKey | undefined;
	numberOfAdditionalProducts: number;
} = () => {
	const [primaryProduct] = usePrimaryProduct();
	const availableProducts = useAvailableProducts();
	const productConfigs = useProductConfigs();

	const apps3PToDisplay = useMemo(
		() =>
			availableProducts
				.filter(is3pProductKey)
				.sort((a, b) => {
					const aPreferredPosition = prioritised3PApps.indexOf(a);
					const bPreferredPosition = prioritised3PApps.indexOf(b);

					if (aPreferredPosition !== -1 && bPreferredPosition !== -1) {
						return aPreferredPosition - bPreferredPosition;
					} else if (aPreferredPosition !== -1) {
						return -1;
					} else if (bPreferredPosition !== -1) {
						return 1;
					} else {
						return 0;
					}
				})
				.map((productKey) => ({
					productKey,
					productConfig: productConfigs[productKey],
				}))
				.slice(0, 2),
		[availableProducts, productConfigs],
	);

	const numberOfAdditionalProducts = availableProducts.length - apps3PToDisplay.length;

	return {
		apps3PToDisplay,
		primaryProduct,
		numberOfAdditionalProducts,
	};
};
